import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { checkSupabaseConnection } from '../lib/supabase';
import { isOfflineModeEnabled, setOfflineMode } from '../lib/offlineStorage';

export const ConnectionStatusBar: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [offlineMode, setOfflineModeState] = useState(isOfflineModeEnabled());
  const [needsSetup, setNeedsSetup] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let checkInterval: NodeJS.Timeout;
    
    const checkConnection = async () => {
      if (!isMounted) return;
      
      setIsChecking(true);
      try {
        // Check if Supabase is properly configured
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        console.log("Initial check with:", { 
          url: supabaseUrl ? "Set" : "Not set", 
          key: supabaseAnonKey ? "Set" : "Not set" 
        });
        
        const missingConfig = !supabaseUrl || !supabaseAnonKey || 
            supabaseUrl === 'https://your-project-id.supabase.co' || 
            supabaseAnonKey === 'your-anon-key-from-supabase-dashboard';
        
        if (missingConfig) {
          if (isMounted) {
            console.warn("Supabase configuration is missing or invalid!");
            setNeedsSetup(true);
            setIsConnected(false);
            setErrorMessage('Supabase configuration is missing or invalid');
            if (!offlineMode) {
              setOfflineModeState(true);
              setOfflineMode(true);
            }
          }
          return;
        } else {
          if (isMounted) {
            setNeedsSetup(false);
          }
        }
        
        const connected = await checkSupabaseConnection();
        if (isMounted) {
          setIsConnected(connected);
          setErrorMessage(connected ? null : 'Unable to connect to Supabase - please check your project status');
          
          // If connection is lost, automatically enable offline mode
          if (!connected && !offlineMode) {
            setOfflineModeState(true);
            setOfflineMode(true);
          }
        }
      } catch (error) {
        console.warn('Connection check error:', error);
        if (isMounted) {
          setIsConnected(false);
          setErrorMessage(error instanceof Error ? error.message : 'Unknown connection error');
          // Enable offline mode on connection error
          if (!offlineMode) {
            setOfflineModeState(true);
            setOfflineMode(true);
          }
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };
    
    // Initial check
    checkConnection();
    
    // Check connection every 30 seconds
    checkInterval = setInterval(checkConnection, 30000);
    
    return () => {
      isMounted = false;
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [offlineMode]);

  const toggleOfflineMode = () => {
    const newMode = !offlineMode;
    setOfflineModeState(newMode);
    setOfflineMode(newMode);
  };

  const handleRetryConnection = () => {
    setIsChecking(true);
    setErrorMessage(null);
    window.location.reload();
  };

  if (needsSetup) {
    return (
      <div className="bg-amber-100 text-amber-700 py-2 px-4 flex items-center justify-center">
        <Database className="h-4 w-4 mr-2" />
        <span className="text-sm">Supabase-Konfiguration fehlt oder ist ungültig - Offline-Modus aktiv</span>
      </div>
    );
  }

  if (isChecking && isConnected === null) {
    return (
      <div className="bg-gray-100 text-gray-700 py-2 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
        <span className="text-sm">Verbindung wird geprüft...</span>
      </div>
    );
  }

  if (isConnected === false) {
    return (
      <div className="bg-amber-100 text-amber-700 py-2 px-4 flex items-center justify-center">
        <AlertCircle className="h-4 w-4 mr-2" />
        <span className="text-sm">
          {errorMessage || 'Datenbankverbindung nicht möglich'} - Offline-Modus aktiv
        </span>
        <button 
          onClick={handleRetryConnection}
          className="ml-2 p-1 hover:bg-amber-200 rounded-full transition-colors"
          title="Verbindung erneut prüfen"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isChecking ? 'animate-spin' : ''}`} />
        </button>
      </div>
    );
  }

  if (isConnected === true) {
    return (
      <div className={`${offlineMode ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'} py-2 px-4 flex items-center justify-center`}>
        {offlineMode ? (
          <>
            <Database className="h-4 w-4 mr-2" />
            <span className="text-sm">Offline-Modus aktiv (Datenbank verfügbar)</span>
            <button 
              onClick={toggleOfflineMode}
              className="ml-2 text-xs bg-amber-200 hover:bg-amber-300 px-2 py-0.5 rounded transition-colors"
            >
              Online-Modus aktivieren
            </button>
          </>
        ) : (
          <>
            <CheckCircle className="h-4 w-4 mr-2" />
            <span className="text-sm">Mit Datenbank verbunden</span>
            <button 
              onClick={toggleOfflineMode}
              className="ml-2 text-xs bg-green-200 hover:bg-green-300 px-2 py-0.5 rounded transition-colors"
            >
              Offline-Modus aktivieren
            </button>
          </>
        )}
      </div>
    );
  }

  return null;
};